import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  CareerOverviewCompensationProfile,
  CareerOverviewGovernanceRole,
  CareerOverviewJobApplication,
  CareerOverviewJobPreferences,
  CareerOverviewPerson
} from "./_components/careerOverviewUtils";

export type CareerOverviewQueryData = {
  person: CareerOverviewPerson & {
    id: string;
    totalJobApplications?: number | null;
    totalActiveJobApplications?: number | null;
    jobPreferences?: CareerOverviewJobPreferences | null;
    jobApplications?: CareerOverviewJobApplication[];
    compensationProfiles?: CareerOverviewCompensationProfile[];
    facilityGovernanceRoles?: CareerOverviewGovernanceRole[];
  };
};

export type CareerOverviewQueryVariables = {
  pgaId: string;
  includeCareerPreferences: boolean;
  includeJobApplications: boolean;
  includeCompensationProfiles: boolean;
  includeFacilityGovernance: boolean;
};

export const GET_CAREER_OVERVIEW_QUERY: TypedDocumentNode<CareerOverviewQueryData, CareerOverviewQueryVariables> = gql`
  query GetCareerOverview(
    $pgaId: ID!
    $includeCareerPreferences: Boolean!
    $includeJobApplications: Boolean!
    $includeCompensationProfiles: Boolean!
    $includeFacilityGovernance: Boolean!
  ) {
    person(pgaId: $pgaId) {
      id
      pgaId
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          id
          classification {
            id
            name
          }
        }
      }
      careerInfo {
        hasCompensationProfile
        contactLastModifiedCompensationProfile
        hasJobPreferences
        jobPreferencesLastModified
        currentEmployerContainsGolfDirector
      }
      primarySection {
        id
        name
        careerConsultant {
          id
          name
        }
      }
      employments {
        id
        isPrimary
        title
        formattedTitle
        jobClassification
        startedOn
        endedOn
        facility {
          id
          name
        }
      }
      totalJobApplications @include(if: $includeJobApplications)
      totalActiveJobApplications @include(if: $includeJobApplications)
      jobPreferences @include(if: $includeCareerPreferences) {
        id
        jobCompensationMinimum
        jobSearchStatus
        optedInToJobSeekerEmails
        updatedAt
      }
      jobApplications @include(if: $includeJobApplications) {
        id
        appliedOn
        active
        reviewScoreAverage
        jobPost {
          id
          name
          facility {
            id
            name
          }
        }
      }
      compensationProfiles @include(if: $includeCompensationProfiles) {
        id
        isSnapshot
        updatedAt
        contactLastModifiedDate
        lastModificationSource
        basePay
        basePayType
        totalCompensation
        currentEmployerContainsGolfDirector
      }
      facilityGovernanceRoles @include(if: $includeFacilityGovernance) {
        id
        status
        title {
          id
          title
        }
        facility {
          id
          name
        }
      }
    }
  }
`;

export const GET_CAREER_OVERVIEW_JOB_POSTS_QUERY: TypedDocumentNode<
  {
    jobPosts: {
      totalCount: number;
    };
  },
  { employerId: string; stages: string[] }
> = gql`
  query CareerOverviewJobPosts($employerId: ID!, $stages: [String!]) {
    jobPosts(first: 1, input: { employerId: $employerId, stage: $stages }) {
      totalCount
    }
  }
`;

export const GET_JOB_APPLICATIONS_QUERY = gql`
  query GetJobApplications($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      latestProgram {
        id
        programType {
          id
          name
        }
      }
      primarySection {
        id
        name
      }
      profilePhoto
      displayName
      jobApplications {
        id
        appliedOn
        resume
        coverLetter
        website
        active
        email
        phone
        reviewScoreAverage
        reviews(pgaId: $pgaId) {
          id
          createdAt
          updatedAt
          comments
          score
          isRecommendation
          reviewedBy {
            id
            name
            email
          }
        }
        jobPost {
          id
          name
          serviceLevel
          facility {
            id
            name
          }
        }
        applicant {
          id
          firstName
          lastName
        }
      }
      totalJobApplications
      totalActiveJobApplications
    }
  }
`;

export const GET_JOB_PREFERENCES_QUERY = gql`
  query GetJobPreferences($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      jobPreferences {
        id
        contact {
          id
          firstName
          lastName
        }
        createdAt
        interestInInternationalPositions
        jobCategories
        jobCompensationMaximum
        jobCompensationMinimum
        jobSearchStatus
        locationPreferences
        openToSeasonJobs
        optedInToJobSeekerEmails
        updatedAt
      }
    }
  }
`;

export const GET_COMPENSATION_PROFILES_QUERY = gql`
  query GetCompensationProfiles($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      compensationProfiles {
        id
        createdAt
        updatedAt
        isSnapshot
        currentEmployerContainsGolfDirector
        employerAtTimeOfRecord {
          id
          name
        }
        contactLastModifiedDate
        memberClassificationAtTimeOfRecord
        contact {
          id
          firstName
          lastName
        }
        lastModificationSource
        creationSource
        basePay
        basePayType
        payRate
        bonusIncome
        lessonIncome
        otherIncome
        initialTotalCompensation
        totalCompensation
        totalMonthsWorkedAnnual
        employerOffers401k
        employerMatches401k
        employerMatch401kPercent
        employerPaysPgaDues
        employerOffersHealthcare
        employerOffersHealthcareUsed
        employerOffersDental
        employerOffersVision
        employerOffersLifeInsurance
        employerOffersDisability
        employerOffersPaidVacation
        employerOffersPaidVacationWeekAmount
        employerOffersEducationAllowance
        employerOffersEducationAllowanceAmount
        employerOffersTournamentAllowance
        employerOffersTournamentAllowanceAmount
        employerOffersCellphoneAllowance
        employerOffersCellphoneAllowanceAmount
        employerOffersCarAllowance
        employerOffersCarAllowanceAmount
        employerOffersClothingAllowance
        employerOffersClothingAllowanceAmount
        participateInGrp
        otherEmploymentBenefitsDescription
        golfShopOwnershipPercent
        golfShopGrossSales
        golfShopNetIncome
        golfShopCommissionPercentage
        golfShopCommissionAnnualIncome
        ownsGolfShop
        pgaJrlIncome
        privateLessonIncome
        groupLessonIncome
        otherLessonIncome
        lessonPercentageSplit
        eventOutingCommissionAmount
        eventOutingCommissionPercent
        facilityCommissionAmount
        facilityCommissionPercent
        foodBeverageCommissionAmount
        foodBeverageCommissionPercent
        golfCarCommissionAmount
        golfCarCommissionPercent
        greenFeeCommissionAmount
        greenFeeCommissionPercent
        membershipSalesCommissionAmount
        membershipSalesCommissionPercent
        practiceRangeCommissionAmount
        practiceRangeCommissionPercent
        clubFittingCommissionAmount
        clubFittingCommissionPercent
        clubRentalCommissionAmount
        clubRentalCommissionPercent
        clubRepairCommissionAmount
        clubRepairCommissionPercent
        clubStorageCommissionAmount
        clubStorageCommissionPercent
      }
    }
  }
`;

export const UPDATE_JOB_PREFERENCES_MUTATION = gql`
  mutation PatchJobPreferences($input: BackofficeJobPreferencesInput!) {
    patchJobPreferences(input: $input) {
      success
      message
    }
  }
`;

export const CREATE_JOB_PREFERENCES_MUTATION = gql`
  mutation CreateJobPreferences($input: BackofficeCreateJobPreferencesInput!) {
    createJobPreferences(input: $input) {
      success
      message
      jobPreferences {
        id
        contact {
          id
          firstName
          lastName
        }
        createdAt
        interestInInternationalPositions
        jobCategories
        jobCompensationMaximum
        jobCompensationMinimum
        jobSearchStatus
        locationPreferences
        openToSeasonJobs
        optedInToJobSeekerEmails
        updatedAt
      }
    }
  }
`;

export const UPDATE_JOB_APPLICATION_FILES_MUTATION = gql`
  mutation UpdateJobApplicationFiles($input: BackofficeUpdateJobApplicationFilesInput!) {
    updateJobApplicationFiles(input: $input) {
      success
      message
    }
  }
`;

export const DELETE_JOB_PREFERENCES_MUTATION = gql`
  mutation DeleteJobPreferences($jobPreferencesId: ID!) {
    deleteJobPreferences(jobPreferencesId: $jobPreferencesId) {
      success
      message
    }
  }
`;

export type CareerPlanRoleRubric = {
  id: string;
  title: string;
  classificationLabel: string;
  competencyScores: Array<{
    competencyId: string;
    competencyName: string;
    desiredScore: number;
  }>;
};

export type CareerPlanQueryData = {
  careerRoleRubrics: CareerPlanRoleRubric[];
  person: {
    id: string;
    latestProgram?: {
      band?: {
        classification?: {
          name: string;
        } | null;
      } | null;
    } | null;
    careerIntent?: import("./career-profile/careerPlanHistory").CareerPlanGoal | null;
    careerIntents: import("./career-profile/careerPlanHistory").CareerPlanGoal[];
    experienceIndexAssessment?: import("./career-profile/careerPlanHistory").CareerPlanAssessment | null;
  };
};

export type CareerPlanQueryVariables = {
  pgaId: string;
};

export type CareerPlanHubQueryData = {
  person: {
    id: string;
    careerIntent?: import("./career-profile/careerPlanHistory").CareerPlanGoal | null;
    careerIntents: import("./career-profile/careerPlanHistory").CareerPlanGoal[];
    experienceIndexAssessment?: import("./career-profile/careerPlanHistory").CareerPlanAssessment | null;
    careerPlans: import("./career-profile/careerPlanHubSummaries").HubCareerPlan[];
  };
};
export const CAREER_INTENT_FIELDS = gql`
  fragment CareerIntentFields on BackofficeCareerIntent {
    id
    additionalContext
    archivedAt
    clarity
    createdAt
    facilityTypes
    hasNoDesiredChanges
    improvementAreas
    locations
    motivations
    retirementType
    targetPositions
    timeframe
    updatedAt
  }
`;

export const GET_CAREER_PLAN_QUERY: TypedDocumentNode<CareerPlanQueryData, CareerPlanQueryVariables> = gql`
  query GetCareerPlan($pgaId: ID!) {
    careerRoleRubrics(assessmentKind: EXPERIENCE) {
      id
      title
      classificationLabel
      competencyScores {
        competencyId
        competencyName
        desiredScore
      }
    }
    person(pgaId: $pgaId) {
      id
      latestProgram {
        band {
          classification {
            name
          }
        }
      }
      careerIntent {
        ...CareerIntentFields
      }
      careerIntents {
        ...CareerIntentFields
      }
      experienceIndexAssessment {
        id
        answeredQuestionCount
        totalQuestionCount
        updatedAt
        assessmentComparisonRole
        scores {
          competencyId
          competencyName
          score
          answeredQuestionCount
          totalQuestionCount
          isComplete
        }
        answers {
          competencyId
          competencyName
          questionId
          questionContent
          optionId
          optionContent
        }
      }
    }
  }
  ${CAREER_INTENT_FIELDS}
`;

export const GET_CAREER_PLAN_HUB_QUERY: TypedDocumentNode<CareerPlanHubQueryData, CareerPlanQueryVariables> = gql`
  query GetCareerPlanHub($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      careerIntent {
        ...CareerIntentFields
      }
      careerIntents {
        ...CareerIntentFields
      }
      experienceIndexAssessment {
        id
        answeredQuestionCount
        totalQuestionCount
        updatedAt
        scores {
          competencyId
          competencyName
          score
          answeredQuestionCount
          totalQuestionCount
          isComplete
        }
      }
      careerPlans {
        id
        title
        status
        updatedAt
      }
    }
  }
  ${CAREER_INTENT_FIELDS}
`;
